import DOMPurify from 'dompurify';

/**
 * Configuration options for different sanitization contexts
 */
const sanitizerConfigs = {
  // For plain text - strips all HTML tags
  plainText: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true, // Keep text content even when removing tags
  },
  
  // For rich text - allows safe HTML formatting
  richText: {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span'],
    ALLOWED_ATTR: ['href', 'title', 'target'],
    ALLOW_DATA_ATTR: false,
  },
  
  // For URLs - validates and sanitizes URLs
  url: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  },
};

/**
 * Extracts href values from anchor tags before sanitization
 * Replaces anchor tags with their href values to preserve URLs
 * @param input - The input string potentially containing anchor tags
 * @returns Modified string with anchor hrefs extracted
 */
const extractAnchorsHref = (input: string): string => {
  // Regular expression to match anchor tags with href attributes
  // Matches: <a href="url">text</a> or <a href="url"> or <a href="url" />
  const anchorRegex = /<a\s+(?:[^>]*?\s+)?href=["']([^"']+)["'][^>]*>([^<]*)<\/a>|<a\s+(?:[^>]*?\s+)?href=["']([^"']+)["'][^>]*\/?>/gi;
  
  return input.replace(anchorRegex, (match, href1, text, href2) => {
    // href1 is for closed tags <a href="...">text</a>
    // href2 is for self-closing or unclosed tags <a href="..." />
    const url = href1 || href2;
    
    // If there's text content, keep it with the URL
    if (text && text.trim()) {
      return `${text.trim()} (${url})`;
    }
    
    // If no text content, just return the URL
    return url;
  });
};

/**
 * Sanitizes user input to prevent XSS attacks
 * @param dirty - The potentially unsafe user input
 * @param config - Optional DOMPurify configuration
 * @returns Sanitized safe string
 */
export const sanitizeInput = (
  dirty: string | null | undefined,
  config: DOMPurify.Config = sanitizerConfigs.plainText
): string => {
  if (!dirty) return '';
  
  // Convert to string if not already
  let input = String(dirty);
  
  // For plainText sanitization, extract anchor hrefs before sanitizing
  // This preserves URLs from anchor tags that would otherwise be removed
  if (config === sanitizerConfigs.plainText) {
    input = extractAnchorsHref(input);
  }
  
  // Sanitize the input
  const clean = DOMPurify.sanitize(input, config);
  
  return clean;
};

/**
 * Sanitizes plain text by removing all HTML tags
 * Useful for subjects, names, simple text fields
 */
export const sanitizePlainText = (text: string | null | undefined): string => {
  return sanitizeInput(text, sanitizerConfigs.plainText);
};

/**
 * Sanitizes rich text allowing safe HTML formatting
 * Useful for message bodies that may contain basic formatting
 */
export const sanitizeRichText = (html: string | null | undefined): string => {
  return sanitizeInput(html, sanitizerConfigs.richText);
};

/**
 * Sanitizes and validates URLs
 */
export const sanitizeUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  
  const sanitized = sanitizeInput(url, sanitizerConfigs.url);
  
  // Additional validation: ensure it's a valid URL or relative path
  try {
    // If it starts with http/https/mailto/tel, validate as full URL
    if (/^(https?|mailto|tel):/.test(sanitized)) {
      new URL(sanitized);
    }
    return sanitized;
  } catch {
    // If URL validation fails, return empty string for safety
    console.warn('Invalid URL detected and blocked:', url);
    return '';
  }
};

/**
 * Escapes HTML special characters for safe display
 * Alternative to sanitization when you want to preserve the exact input
 */
export const escapeHtml = (text: string | null | undefined): string => {
  if (!text) return '';
  
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Sanitizes file names to prevent path traversal attacks
 */
export const sanitizeFileName = (fileName: string | null | undefined): string => {
  if (!fileName) return '';
  
  return fileName
    .replace(/[^a-zA-Z0-9._-]/g, '_') // Replace unsafe characters
    .replace(/^\.+/, '') // Remove leading dots
    .replace(/\.{2,}/g, '.') // Replace multiple dots with single dot
    .substring(0, 255); // Limit length
};
